/**
 * The Auth0 connections a login surface offers, and what each one is called.
 *
 * Two surfaces present this choice: the MCP server's own OAuth proxy, which is
 * the first page an MCP-first customer ever sees, and the dashboard's sign-in
 * screen. They have to offer the same providers under the same names, because
 * a customer who signed up with Google on one and is offered "Continue with
 * Microsoft" on the other has been handed two accounts. So the names and the
 * labels live here, once, rather than in each app.
 *
 * The connection *list* is configuration (`LAGOON_MCP_CONNECTIONS`); this file
 * holds only what a name is called and whether it is a staff connection.
 *
 * No imports, on purpose, for the same reason `branding.ts` has none: the
 * browser bundles reach this through the `@lagoon/config/connections` subpath
 * and it must not drag zod or `node:*` in with it.
 */
/**
 * What a connection nobody outside BPG can join is called.
 *
 * Every name that is not one of the customer connections below is treated as
 * a staff connection: today `lagoon-bpg-entra-dev`, later `bpg-internal-ad`.
 * The default is that way round deliberately. A misspelled or newly created
 * customer connection then shows up as a staff door, which is a cosmetic bug
 * on a link customers are not meant to use, rather than a staff directory
 * offered to the world as "Continue with Microsoft".
 */
export const STAFF_CONNECTION_LABEL = "Black Pearl Group staff sign-in";
/**
 * The customer connections, in the order a picker should show them.
 *
 * The labels say what the person will actually be asked for, not what the
 * connection is called: nobody knows they are on `lagoon-users`, they know
 * they are typing an email address.
 */
const CONNECTION_LABELS = Object.freeze({
    "lagoon-entra": "Continue with Microsoft",
    "lagoon-google": "Continue with Google",
    "lagoon-users": "Continue with email",
});
/** What to write on the button for this connection. */
export function connectionLabel(name) {
    return Object.hasOwn(CONNECTION_LABELS, name)
        ? (CONNECTION_LABELS[name] ?? STAFF_CONNECTION_LABEL)
        : STAFF_CONNECTION_LABEL;
}
/**
 * Whether this connection is the staff door.
 *
 * A picker puts these last and smaller: they are a valid way in for the few
 * people who need them and noise for everybody else.
 */
export function isStaffConnection(name) {
    return !Object.hasOwn(CONNECTION_LABELS, name);
}
/**
 * The configured connection names, from a comma-separated setting.
 *
 * Empty is the default and means "offer no choice": the surface falls back to
 * sending no `connection` at all, which is what every deployment did before
 * there was more than one connection to choose between. Duplicates are dropped
 * so that a setting typed twice does not draw the same button twice.
 */
export function parseConnections(raw) {
    return [
        ...new Set((raw ?? "")
            .split(",")
            .map((name) => name.trim())
            .filter((name) => name !== "")),
    ];
}
export function signInCopy(branding) {
    return {
        title: `Sign in to ${branding.productName}`,
        choose: "Choose how you want to sign in. Use the same one every time: " +
            "signing in a different way creates a separate account.",
        staffNote: `Work at ${branding.legalEntity}? Use the staff link below instead.`,
        footer: "Sign in to publish and manage your sites. People you invite to read a site " +
            "do not need an account, and get a code by email instead.",
        returnToClient: "You will return to Claude when you are done.",
    };
}
//# sourceMappingURL=connections.js.map